import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileMenu } from '../Profile/ProfileMenu';
import { IoArrowBack } from 'react-icons/io5';
import { CheckCircle } from 'lucide-react';
import { useGetVotingPollQuery, useGetVotingQuery, useVotingMutation } from '@/store/api/votingApi';
import { buildAssetUrl } from '@/utils/asset';
import { useCurrentUser } from '@/hooks';
import toast from 'react-hot-toast';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const PlayerRow = ({ player, addPoint, removePoint, points, onVoteChange, isDisabled }: any) => {
  const contestant = player?.user || {};

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-[#0A1426] border border-[#1A2C4D] rounded-xl px-4 py-3 w-full">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <img
          src={buildAssetUrl(contestant?.profileImage, '/images/gameSection/user.png')}
          className="w-12 h-12 rounded-full object-cover shrink-0"
          onError={(e) => {
            e.currentTarget.src = '/images/gameSection/user.png';
          }}
        />

        <p className="text-sm break-words leading-tight">
          {contestant?.firstname} {contestant?.lastname}
        </p>
      </div>

      <div className="flex items-center gap-2 p-2 rounded-md bg-[#0A1426] border border-[#1A2C4D] w-full lg:w-auto justify-center lg:justify-start">
        <button onClick={addPoint} disabled={isDisabled} className="px-2">
          +
        </button>

        <input
          type="number"
          min={0}
          value={points || ''}
          placeholder="Add Vote"
          disabled={isDisabled}
          onChange={(e) => onVoteChange(Number(e.target.value))}
          className="w-20 bg-transparent text-center text-[#E4FFEF] outline-none"
        />

        <button onClick={removePoint} disabled={isDisabled} className="px-2">
          -
        </button>
      </div>
    </div>
  );
};

const RoundCard = ({ round }: any) => {
  const [playerPoints, setPlayerPoints] = useState<Record<number, number>>({});
  const [vote] = useVotingMutation();
  const [showPopup, setShowPopup] = useState(false);
  const user = useCurrentUser();

  const {
    data: voteData,
    isLoading: voteLoading,
    refetch: refetchVotes,
  } = useGetVotingQuery({
    sessionId: round.votingSessionId,
    pollId: round.id,
  });

  const hasVoted = Array.isArray(voteData) && voteData.length > 0;

  const players = round?.contestants || [];

  const voteMap = Array.isArray(voteData)
    ? voteData.reduce((acc: any, v: any) => {
        acc[v.pollContestantId] = (acc[v.pollContestantId] || 0) + v.votePower;
        return acc;
      }, {})
    : {};
  const basePower = Number(round?.totalPower || 0);

  const effectiveTotalPower = user?.type === 'Tribe' ? basePower * 2 : basePower;

  const usedPowerFromPoll = hasVoted
    ? Object.values(voteMap).reduce((a: number, b: any) => a + Number(b), 0)
    : 0;

  const totalUsed = hasVoted
    ? usedPowerFromPoll
    : Object.values(playerPoints).reduce((a, b) => a + b, 0);

  const remainingPower = Math.max(0, effectiveTotalPower - totalUsed);

  const addPoint = (playerId: number) => {
    if (totalUsed >= effectiveTotalPower || hasVoted) return;

    setPlayerPoints((prev) => ({
      ...prev,
      [playerId]: (prev[playerId] || 0) + 1,
    }));
  };

  const removePoint = (playerId: number) => {
    if (hasVoted) return;

    setPlayerPoints((prev) => ({
      ...prev,
      [playerId]: Math.max((prev[playerId] || 0) - 1, 0),
    }));
  };

  const onVoteChange = (playerId: number, value: number) => {
    if (hasVoted) return;

    setPlayerPoints((prev) => {
      const sanitized = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
      const prevValue = prev[playerId] || 0;

      const currentTotal = Object.values(prev).reduce((a, b) => a + b, 0);

      const remaining = effectiveTotalPower - (currentTotal - prevValue);

      const nextValue = Math.min(sanitized, Math.max(0, remaining));

      return {
        ...prev,
        [playerId]: nextValue,
      };
    });
  };

  const handleVote = async () => {
    if (hasVoted) return;

    const distributions = Object.entries(playerPoints)
      .filter(([_, p]) => p > 0)
      .map(([userId, p]) => {
        const player = players.find((pl: any) => pl.userId === Number(userId));

        return {
          pollContestantId: player?.id,
          votePower: p,
        };
      });

    if (!distributions.length) return;

    try {
      await vote({
        sessionId: round.votingSessionId,
        pollId: round.id,
        body: { distributions },
      }).unwrap();

      setPlayerPoints({});
      setShowPopup(true);

      refetchVotes();
    } catch (error) {
      const err = error as FetchBaseQueryError & { data?: { message?: string } };
      toast.error(err?.data?.message || 'Voting session is expired');
    }
  };

  return (
    <div className="bg-[#010711] border border-[#1A2C4D] rounded-2xl overflow-hidden shadow-lg transition-transform duration-300 -translate-y-2 hover:-translate-y-4 flex flex-col h-full">
      <div className="text-center text-lg md:text-2xl py-3 border-b border-[#1A2C4D]">
        Total Voting Points: {remainingPower}{' '}
      </div>

      <div className="relative w-full">
        <img
          src={buildAssetUrl(round?.image, '/images/gameSection/smallhammer.png')}
          alt="Throne"
          className="w-full h-[220px] sm:h-[240px] md:h-[260px] object-cover rounded-[20px]"
          onError={(e) => {
            e.currentTarget.src = '/images/gameSection/smallhammer.png';
          }}
        />
        <div className="absolute inset-0 rounded-[20px] bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        <div className="absolute bottom-6 left-4 text-white">
          <h3 className="text-xl md:text-2xl font-semibold">{round?.title}</h3>

          <p className="text-sm text-white/70 line-clamp-2">{round?.description}</p>
        </div>
      </div>

      <div
        className={`px-4 sm:px-6 pb-4 pr-2 min-h-[200px] max-h-[200px] overflow-y-auto scrollbar-hide scroll-smooth thin-scrollbar flex-1 ${
          players.length === 0 ? 'flex items-center justify-center' : 'space-y-6'
        }`}
      >
        {players.length === 0 ? (
          <p className="text-white/50 text-center">No players</p>
        ) : (
          players.map((player: any) => (
            <PlayerRow
              key={player.id}
              player={player}
              isDisabled={hasVoted}
              addPoint={() => addPoint(player.userId)}
              removePoint={() => removePoint(player.userId)}
              points={hasVoted ? voteMap[player.id] || 0 : playerPoints[player.userId] || 0}
              onVoteChange={(value: number) => onVoteChange(player.userId, value)}
            />
          ))
        )}
      </div>

      {players.length > 0 && (
        <div className="px-4 sm:px-6 pb-6">
          <button
            onClick={handleVote}
            disabled={hasVoted || voteLoading}
            className={`px-10 py-3 text-black rounded-md w-full font-medium transition ${
              hasVoted ? 'bg-gray-500 cursor-not-allowed' : 'bg-white px-10 py-3 hover:bg-gray-200'
            }`}
          >
            {hasVoted ? 'Already Voted' : 'Vote Now'}
          </button>
        </div>
      )}

      <VoteSuccessPopup open={showPopup} onClose={() => setShowPopup(false)} />
    </div>
  );
};

interface RoundVotingProps {
  id: number;
  close: () => void;
}

export default function RoundVoting({ close, id }: RoundVotingProps) {
  const navigate = useNavigate();

  const { data } = useGetVotingPollQuery(id);
  const rounds =
    data?.polls?.map((poll: any) => ({
      id: poll.id,
      title: poll.title,
      description: poll.description,
      image: poll.banner,
      votingSessionId: data.id,
      totalPower: poll.votePower,

      contestants: poll.pollContestants.map((pc: any) => ({
        id: pc.id,
        userId: pc.contestant.id,
        totalVotePower: pc.totalVotePower,

        user: {
          firstname: pc.contestant.firstname,
          lastname: pc.contestant.lastname,
          profileImage: pc.contestant.profileImage,
        },
      })),
    })) || [];
  return (
    <div className="min-h-screen bg-[#05071A] text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#FF5900] opacity-20 blur-[200px]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2C3FC2] opacity-20 blur-[220px]" />

      <header className="relative w-full border-b border-white/10 px-4 sm:px-6 md:px-12 py-4 flex items-center justify-between">
        <img
          src="/images/logo.svg"
          className="max-w-[150px] sm:max-w-[180px] md:max-w-[230px] cursor-pointer"
          onClick={() => navigate('/')}
        />

        <ProfileMenu />
      </header>

      <button
        onClick={close}
        className="flex items-center gap-2 text-white px-4 py-2 ml-4 mt-4 rounded-lg hover:bg-white/10 transition cursor-pointer -translate-y-2"
      >
        <IoArrowBack size={24} />
        Back
      </button>

      <div className="text-center mb-12 md:mb-16 px-4">
        <h1 className="text-lg sm:text-3xl md:text-4xl font-irish leading-snug">{data?.title}</h1>
      </div>

      <div className="max-w-[1500px] z-10 px-4 mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 lg:gap-10 pb-20 font-Gabarito">
        {rounds.map((round: any) => (
          <RoundCard key={round.id} round={round} />
        ))}
      </div>
    </div>
  );
}

interface VoteSuccessPopupProps {
  open: boolean;
  onClose: () => void;
}

const VoteSuccessPopup = ({ open, onClose }: VoteSuccessPopupProps) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
      <div className="bg-[#0A1426] border border-[#1A2C4D] rounded-2xl p-8 w-[320px] text-center shadow-xl animate-scaleIn">
        <CheckCircle className="mx-auto text-green-500" size={60} />

        <h2 className="text-white text-xl font-semibold mt-4">Vote Submitted!</h2>

        <p className="text-white/70 text-sm mt-2">Your vote has been recorded.</p>

        <button
          onClick={onClose}
          className="mt-6 bg-white text-black px-6 py-2 rounded-md font-medium hover:bg-gray-200 transition"
        >
          OK
        </button>
      </div>
    </div>
  );
};
